import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Grid } from "@/comn/components";
import { comnUtils, comnEnvs } from "@/comn/utils"; // 프로젝트 공통 유틸
import { Page, Group, Layout, Button, FormControl } from "@/comn/components"; // 화면 구성 컴포넌트
import { useFetch, useForm, useGrid, useStore, useToast, useResource, useModal, usePage, usePopup } from "@/comn/hooks";
import { BASE, URLS, APIS, SF_LCNE_USR_INFO } from "@/tra/tancis/ptli/usr/lcneusr/services/PtliLcneUsrService";
import { LCNE_USE_TP_CD_UTIL } from "@/tra/tancis/ptli/usr/lcneusr/services/PtliLcneUsrService";
import { set } from "lodash";

/*
 * License User Edit
 * !== 면허 사용자 수정 ==!
 */
export const PTLI0906003S = (): JSX.Element => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0015", "auth_lcmi0001", "auth_ptli0025", "auth_eswi0001", "auth_ptlianyADM"],
    });
    const { t } = useTranslation();
    const navigate = useNavigate();
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { openPopup } = usePopup();
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast();
    const { lcneCoAplyId } = useParams(); // Key Information of this Component Router !== 라우터에 정의된 키정보 ==!
    const [lcneTpCd, setLcneTpCd] = useState<string[]>([]);

    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    const { getResource } = useResource({
        defaultSchema: [{ area: "comnCd", comnCd: "LCM0001" }, { area: "cstmCd" }],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // License User Information !== 면허 사용자 정보 ==!
        lcneUsrInfo: useForm({
            defaultSchema: SF_LCNE_USR_INFO,
        }),
    };

    const coTin = form.lcneUsrInfo.watch("coTin");
    const ectYn = form.lcneUsrInfo.watch("ectYn");

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Get License User Information !== 면허 사용자 정보 조회 ==!
        getLcneUsrInfo: useFetch({
            api: (data) => APIS.getLcneUsrInfo(lcneCoAplyId),
            enabled: !!lcneCoAplyId,
            onSuccess: (data) => {
                const lcneUsr = data.lcneUsr.content;
                const lcneTpComnCdList = getResource("comnCd", "LCM0001");
                const lcneTpCdArr = LCNE_USE_TP_CD_UTIL.initLcneUseTpCdSchema({
                    lcneUseTpCdArray: lcneUsr.lcneTpCdArray,
                    form: form.lcneUsrInfo,
                    lcneTpComnCdList: lcneTpComnCdList,
                    isEditeable: true,
                    t: t,
                });
                form.lcneUsrInfo.setValues(lcneUsr);

                //라이선스 사용자 신청내용 수정시 비밀번호는 단방향 암호화기 때문에 복호화가 불가. 다시 입력해야함
                form.lcneUsrInfo.setValue("coPwd", null);
            },
            onError: (error) => {},
            showToast: true,
        }),
        // Update License User !== 면허 사용자 등록 ==!
        updateLcneUsr: useFetch({
            api: (data) => APIS.updateLcneUsr({ ...data }),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.com.00062", // Successfully saved., !== 정상적으로 저장되었습니다. ==!
                    onConfirm: () => {
                        navigate(`${URLS.ptli0906004q}/${lcneCoAplyId}`);
                    },
                    onCancel: () => {
                        navigate(`${URLS.ptli0906004q}/${lcneCoAplyId}`);
                    },
                });
            },
            onError: (error) => {
                console.log(error);
                if (error.response.status === 400) {
                    console.log(error.response.data);
                    form.lcneUsrInfo.setErrors(error.response.data.errors);
                }

                modal.openModal({
                    content: "msg.00006",
                });
            },
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        checkPwd: () => {
            const coPwd = form.lcneUsrInfo.getValue("coPwd");
            const coPwdCfrm = form.lcneUsrInfo.getValue("coPwdCfrm");

            if (!comnUtils.isEmpty(coPwd) && !comnUtils.equals(coPwd, coPwdCfrm)) {
                form.lcneUsrInfo.setError("coPwdCfrm", {
                    message: "msg.ptl.00712", // Passwords do not match. !== 비밀번호가 일치하지 않습니다. ==!
                    type: "error",
                });
            } else {
                form.lcneUsrInfo.clearErrors("coPwdCfrm");
            }
        },
        // Update License User !== 면허 사용자 수정 ==!
        updateLcneUsr: () => {
            if (!comnUtils.isEmpty(form.lcneUsrInfo.errors)) {
                toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                return;
            }
            form.lcneUsrInfo.handleSubmit(
                () => {
                    const data = form.lcneUsrInfo.getValues();

                    // const lcneTpCdArr = form.lcneUsrInfo.getValue("lcneTpCd");
                    // const bizTpCd = form.lcneUsrInfo.getValue("bizTpCd");
                    // const checkOnlyCOT = lcneTpCdArr?.includes("5") && lcneTpCdArr?.length === 1;
                    // if (bizTpCd === "007") {
                    //     if (!checkOnlyCOT) { // business Type = 007 이면서 TaxPayerType이 business만 가능하다 + business type 007 이외의 아무 값
                    //         toast.showToast({ type: "warning", content: "msg.ptl.00823" }); // false가 난다면 에러메세지 출력, 아니면 통과
                    //         return;
                    //     }
                    // }

                    data.lcneTpCdArray = LCNE_USE_TP_CD_UTIL.makeLcneUseTpCdDtoArray(form.lcneUsrInfo, "BASE");
                    modal.openModal({
                        content: "msg.00101", // Do you want to save ? !== 저장 하시겠습니까 ? ==!
                        onConfirm: () => {
                            fetch.updateLcneUsr.fetch(data);
                        },
                    });
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                },
            )();
        },
        onUsrStatChange: (value: any) => {
            if (value == 2) {
                form.lcneUsrInfo.setSchema("mdfyRsn", { required: true });
            } else {
                form.lcneUsrInfo.setSchema("mdfyRsn", { required: false });
            }
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        fetch.getLcneUsrInfo.fetch();
    }, []);

    const lcneTpCdAct = form.lcneUsrInfo.watch("lcneTpCd");
    useEffect(() => {
        setLcneTpCd(lcneTpCdAct);
    }, [lcneTpCdAct]);

    return (
        <Page>
            <form>
                <Group>
                    <Group.Body>
                        <Group.Section>
                            <Group.Row>
                                <Group.Control {...form.lcneUsrInfo.schema.coTin} edit={false} />
                                <Group.Control {...form.lcneUsrInfo.schema.coNm}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.lcneUsrInfo.schema.cstmOfceCd}></Group.Control>
                                <Group.Control
                                    {...form.lcneUsrInfo.schema.usrStatCd}
                                    onChange={handler.onUsrStatChange}
                                ></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.lcneUsrInfo.schema.rprsTelno}></Group.Control>
                                <Group.Control {...form.lcneUsrInfo.schema.rprsFaxNo}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.lcneUsrInfo.schema.rprsEml}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.lcneUsrInfo.schema.coAddr}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.lcneUsrInfo.schema.coPwd}></Group.Control>
                                <Group.Control
                                    {...form.lcneUsrInfo.schema.coPwdCfrm}
                                    onBlur={() => {
                                        handler.checkPwd();
                                    }}
                                />
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.lcneUsrInfo.schema.ectYn}></Group.Control>
                            </Group.Row>
                            {ectYn === "N" && (
                                <Group.Row>
                                    <Group.Control {...form.lcneUsrInfo.schema.lcneTpCd}></Group.Control>
                                </Group.Row>
                            )}
                            {ectYn === "N" &&
                                LCNE_USE_TP_CD_UTIL.isLcneUseTpCdTarget({
                                    lcneTpCd: "1",
                                    lcneTpCdChekedArr: form.lcneUsrInfo.getValue("lcneTpCd"),
                                }) && (
                                    <Group.Row>
                                        <Group.Control
                                            {...form.lcneUsrInfo.schema[LCNE_USE_TP_CD_UTIL.getLcneUseTpCdVarNm("1")]}
                                            required={true}
                                        ></Group.Control>
                                    </Group.Row>
                                )}
                            <Group.Row>
                                <Group.Control {...form.lcneUsrInfo.schema.mdfyRsn}></Group.Control>
                            </Group.Row>
                        </Group.Section>
                    </Group.Body>
                </Group>
            </form>
            <Layout direction="row">
                <Layout.Left>
                    <Button
                        role="list"
                        onClick={() => {
                            navigate(URLS.ptli0906001q);
                        }}
                    ></Button>
                </Layout.Left>
                <Layout.Right>
                    <Button
                        role="save"
                        onClick={() => {
                            handler.updateLcneUsr();
                        }}
                    >
                        {t("B_SAVE")}
                    </Button>
                </Layout.Right>
            </Layout>
        </Page>
    );
};
